'use client';

import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import useSearchEvent from 'hooks/useSearchEvent';
import SearchEventList from 'components/SearchEventList/SearchEventList';
import SearchContent from 'components/Shared/SearchContent/SearchContent';
import { SearchEventResult } from 'searchEvent.d';
import { ParamsResult } from 'ParamsResult.d';
import { getDateSearch, updateUrlQueryParam } from 'utils/misc';
import { componentTrackingSearch, urlParamsSearch } from 'constants/search';
import { listPredefinedDates } from 'constants/listPredefinedDates';
import getSearchSpecificTerms from 'components/SearchSpecificTerms/getSearchSpecificTerms';
import { Title } from 'components/SearchEventList/SearchList.css';

export type SearchEventTemplateProps = {
  params: ParamsResult;
  searchDataResult: SearchEventResult;
  otherSearchDataResult?: SearchEventResult;
  termURL: string;
  isMobile: boolean;
  dateURL?: string | string[];
};

function SearchEventTemplate({
  params, searchDataResult, otherSearchDataResult, dateURL, termURL, isMobile,
}: SearchEventTemplateProps) {
  const searchEventStore = useSearchEvent();
  searchEventStore.hydrate(searchDataResult);
  if (otherSearchDataResult) searchEventStore.hydrateOtherResult(otherSearchDataResult);

  useEffect(() => {
    if (!dateURL && (params?.url_params?.date?.slug || params?.url_params?.component?.slug)) {
      const slugDate = params?.url_params?.date?.slug || params?.url_params?.component?.slug || '';
      const isDate = listPredefinedDates.some((option) => slugDate.includes(option.dateId));
      if (isDate) updateUrlQueryParam({ param: urlParamsSearch.date, value: getDateSearch(slugDate) });
    }
  }, [dateURL, params?.url_params?.date?.slug, params?.url_params?.component?.slug]);

  const ComponentSpecificTerms = getSearchSpecificTerms(termURL);

  return (
    <>
      {ComponentSpecificTerms}
      <SearchContent
        pageTypeText="eventos"
        searchResult={searchEventStore?.searchDataResult}
        otherSearchResult={searchEventStore?.otherSearchResult}
        totalPages={searchEventStore?.totalPagesOtherResult || searchEventStore?.totalPages}
        isFetching={searchEventStore.isFetching}
        isMobile={isMobile}
        onChangePage={(page: string | number) => {
          if (!isMobile) {
            updateUrlQueryParam({ param: urlParamsSearch.page, value: String(page) });
          }

          searchEventStore.fetchPaginationData({
            query: window?.location?.search,
            isMobile,
            page,
            onSuccess: () => window?.scrollTo({ top: 0, left: 0 }),
          });
          return +page;
        }}
      >
        {searchEventStore?.searchDataResult?.data?.length ? (
          <SearchEventList events={searchEventStore?.searchDataResult?.data} serviceParams={params} isMobile={isMobile} />
        ) : null}

        {searchEventStore?.otherSearchResult?.data?.length ? (
          <>
            <h2 className={Title}>
              {searchEventStore?.searchDataResult?.data?.length ? `${termURL} em outras cidades` : 'Eventos em outras cidades'}
            </h2>
            <SearchEventList
              events={searchEventStore?.otherSearchResult?.data}
              serviceParams={params}
              isMobile={isMobile}
              tracking={{
                label: searchEventStore?.searchDataResult?.data?.length
                  ? componentTrackingSearch.termOtherCities
                  : componentTrackingSearch.termNoResultOtherCities,
              }}
            />
          </>
        ) : null}
      </SearchContent>
    </>
  );
}

export default observer(SearchEventTemplate);
